/**
 * MapHeroSection Component
 * 
 * Wraps PolylineMap with hero section styling and collapse/expand functionality.
 * Provides progressive disclosure pattern for the activity map.
 */

import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { LocationOff } from '@mui/icons-material';
import { PolylineMap } from './PolylineMap';
import { type ActivityDetailResponse } from '../../../types';

interface MapHeroSectionProps {
  /** Activity data containing polyline and details */
  activity: ActivityDetailResponse | null;
  /** Whether the activity data is loading */
  isLoading?: boolean;
}

/**
 * MapHeroSection - Hero map display with collapsible functionality
 */
export const MapHeroSection: React.FC<MapHeroSectionProps> = ({
  activity,
  isLoading = false,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const defaultHeight = 600; // Default expanded size
  
  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  // Extract polyline from activity maps (same as legacy implementation)
  const polyline = activity?.maps?.polyline || activity?.maps?.summary_polyline;
  
  const hasGpsData = Boolean(polyline);

  // Loading state
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: defaultHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          borderRadius: 0,
        }}
      >
        <CircularProgress size={40} />
      </Paper>
    );
  }

  // No GPS data state
  if (!hasGpsData) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: defaultHeight,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 0,
          gap: 2,
        }}
      >
        <LocationOff sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          No GPS data available for this activity
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Default Map View */}
      {!isFullscreen && (
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            boxShadow: 2,
          }}
        >
          <PolylineMap
            polyline={polyline}
            height={defaultHeight}
            width="100%"
            strokeWidth={3}
            minimal={false}
            isHeroMode={true}
            isFullscreen={false}
            onToggleFullscreen={toggleFullscreen}
          />
        </Box>
      )}

      {/* Fullscreen Map View */}
      {isFullscreen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            bgcolor: 'background.default',
          }}
        >
          <PolylineMap
            polyline={polyline}
            height="100vh"
            width="100vw"
            strokeWidth={3}
            minimal={false}
            isHeroMode={true}
            isFullscreen={true}
            onToggleFullscreen={toggleFullscreen}
          />
        </Box>
      )}
    </>
  );
};

export default MapHeroSection;
